import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  FormControl,
  TextField,
  alpha,
} from '@mui/material';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import FormButtonControls from '@/components/forms/formButtonControls/FormButtonControls';

const ColorPickerForm = ({
  app,
  deployment,
  next,
  back,
  isLastStep,
  isFirstStep,
  handleCancel,
}) => {
  const [selectedColor, setSelectedColor] = useState(
    deployment?.config?.themeColor || '#000000',
  );
  const [error, setError] = useState(false);

  const handleColorChange = (event) => {
    const color = event.target.value;
    setSelectedColor(color);
    setError(!/^#[0-9A-F]{6}$/i.test(color));
  };

  const handleSave = () => {
    if (error || !selectedColor) {
      setError(true);
    } else {
      next({ themeColor: selectedColor });
    }
  };

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        overflow: 'visible',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <CardHeader
        avatar={<ColorLensIcon color="primary" />}
        title={<Typography variant="h6">Farbauswahl</Typography>}
        subheader={`Für ${app?.title || deployment?.title}`}
        sx={{
          bgcolor: (theme) => alpha(theme.palette.primary.light, 0.1),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      />

      <CardContent sx={{ pt: 3, pb: 2, flex: 1 }}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Wählen Sie eine Farbe für {app?.title || deployment?.title}.
        </Typography>

        <FormControl fullWidth>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              sx={{ flex: 1 }}
              label="Farbe (Hex)"
              value={selectedColor}
              onChange={handleColorChange}
              error={error}
              helperText={
                error
                  ? 'Bitte geben Sie einen gültigen Hex-Farbwert ein (z.B. #FF0000)'
                  : ''
              }
              placeholder="#000000"
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <input
                type="color"
                value={selectedColor}
                onChange={(e) => setSelectedColor(e.target.value)}
                style={{
                  width: '40px',
                  height: '40px',
                  padding: 0,
                  border: 'none',
                  cursor: 'pointer',
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Farbwähler
              </Typography>
            </Box>
          </Box>
        </FormControl>

        {selectedColor && !error && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Ausgewählte Farbe:
            </Typography>
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: (theme) =>
                  alpha(theme.palette.background.default, 0.7),
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 0.5,
                  bgcolor: selectedColor,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
              <Typography>{selectedColor.toUpperCase()}</Typography>
            </Box>
          </Box>
        )}
      </CardContent>

      <Divider />

      <CardActions sx={{ p: 2, justifyContent: 'flex-end' }}>
        <FormButtonControls
          next={next && handleSave}
          back={back}
          cancel={handleCancel}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          disabled={error}
        />
      </CardActions>
    </Card>
  );
};

export default ColorPickerForm;
